import { deflateSync, inflateSync, strFromU8, strToU8 } from 'fflate'

export const COMPRESSED_QUERY_PARAMETER_PREFIX = 'c.'

/**
 * Below this length, zlib framing + base64 + the `c.` prefix cannot beat raw JSON,
 * so we skip deflate entirely (common for tab ids, date ranges, small filters).
 */
const MIN_COMPRESSIBLE_JSON_LENGTH = 50

/** Stay under the apply/spread argument limit while avoiding a per-byte string array. */
const BYTES_TO_BINARY_CHUNK_SIZE = 0x8000

const bytesToBinaryString = (bytes: Uint8Array): string => {
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += BYTES_TO_BINARY_CHUNK_SIZE) {
    binary += String.fromCodePoint(...bytes.subarray(offset, offset + BYTES_TO_BINARY_CHUNK_SIZE))
  }
  return binary
}

const uint8ToBase64Url = (bytes: Uint8Array): string =>
  btoa(bytesToBinaryString(bytes)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '')

const base64UrlToUint8 = (value: string): Uint8Array => {
  const padded = value.replaceAll('-', '+').replaceAll('_', '/')
  const padLength = (4 - (padded.length % 4)) % 4
  const binary = atob(`${padded}${'='.repeat(padLength)}`)
  return Uint8Array.from(binary, (character) => character.codePointAt(0) ?? 0)
}

const compressJson = (json: string): string => {
  const compressedBytes = deflateSync(strToU8(json))
  return `${COMPRESSED_QUERY_PARAMETER_PREFIX}${uint8ToBase64Url(compressedBytes)}`
}

/**
 * JSON-encodes a query value, then deflates it when the compressed form is shorter.
 */
export const encodeQueryParameterValue = (value: unknown): string => {
  const json = JSON.stringify(value)
  if (json.length < MIN_COMPRESSIBLE_JSON_LENGTH) {
    return json
  }

  const compressed = compressJson(json)
  return compressed.length < json.length ? compressed : json
}

/**
 * Decodes a query value written by {@link encodeQueryParameterValue}, including legacy uncompressed JSON.
 */
export const decodeQueryParameterValue = (raw: string): unknown => {
  if (!raw.startsWith(COMPRESSED_QUERY_PARAMETER_PREFIX)) {
    return JSON.parse(raw)
  }

  const payload = raw.slice(COMPRESSED_QUERY_PARAMETER_PREFIX.length)
  const json = strFromU8(inflateSync(base64UrlToUint8(payload)))
  return JSON.parse(json)
}
